/**
 * InvokeSkillEffect — Conjuration evolution: replaces Summon with a tracking wisp.
 *
 * Invoke supersedes Summon at XP threshold 200. The invoked wisp is sturdier
 * (22 HP baseline vs. Summon's 10 HP), more convincing to enemies (80% aggro
 * redirect vs. 60%), and uses A* pathfinding to pursue targets instead of
 * simple vector drift.
 *
 * Inherits all `summon` upgrade effects through the `replacesId: 'summon'` chain:
 * - Outstretched (summon_reach): spawnDistanceTiles +1, driftSpeedMultiplier ×1.25
 * - Tenacious (summon_tenacity): hpMultiplier ×1.4, aggroRedirectChance max 0.75
 * Resolver queries use scope 'summon' so existing upgrade wiring applies without
 * modification.
 *
 * Aggro: max(0.8, aggroRedirectChance) — baseline is 0.8 so Tenacious's 0.75
 * does not reduce it; if a future upgrade contributes > 0.8, it takes effect.
 *
 * HP example with Tenacious inherited: round(22 × 1.4) = 31.
 *
 * Fizzle gate: at low Invoke XP the wisp may fail to manifest cleanly —
 * this is the evolution freshness signal (same pattern as Grit evolutions).
 * `targeting.shouldFizzle()` encapsulates the XP-based probability check.
 * A fizzle consumes the cooldown but spawns no wisp.
 *
 * Fast cooldown tier (800ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Invoke
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { WispSummonConfig } from './SummonSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface InvokeDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string, base?: number): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
    getFacingDirection(): { x: number; y: number };
  };
  readonly collision: {
    isWallAt(x: number, y: number): boolean;
  };
  readonly targeting: {
    /** Returns true if the wisp should fail to manifest (XP-based freshness check). */
    shouldFizzle(): boolean;
  };
  readonly summons: {
    spawn(config: WispSummonConfig, x: number, y: number): string;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface InvokeSkillConfig {
  readonly cooldownMs: number;
  /** Invoke-tier wisp HP baseline (22 per spec). */
  readonly baseHp: number;
  /** Invoke-tier aggro weight (80% redirect). */
  readonly baseAggroWeight: number;
  readonly baseSpawnDistanceTiles: number;
  readonly baseDriftSpeed: number;
  readonly leashRadiusTiles: number;
  readonly tileSize: number;
}

const DEFAULT_CONFIG: InvokeSkillConfig = {
  cooldownMs: 800,               // fast cooldown tier
  baseHp: 22,                    // spec § Invoke HP: 22 (design doc value)
  baseAggroWeight: 0.8,          // 80% aggro redirect at Invoke tier
  baseSpawnDistanceTiles: 1,     // base 1 tile (Outstretched adds +1)
  baseDriftSpeed: 1.0,           // integration layer applies px/s
  leashRadiusTiles: 8,
  tileSize: 32,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class InvokeSkillEffect implements SkillEffect {
  readonly skillId = 'invoke';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: InvokeDeps;
  private readonly config: InvokeSkillConfig;

  private _cooldownRemaining = 0;

  // Observable state for scenarios and integration layer
  private _spawnsCount = 0;
  private _lastSpawnedId = '';
  private _lastSpawnX = 0;
  private _lastSpawnY = 0;
  private _lastSpawnWasFallback = false;
  private _fizzled = false;
  private _resolvedHp = 0;
  private _resolvedAggroWeight = 0;
  private _resolvedDriftSpeed = 0;
  private _resolvedSpawnDistanceTiles = 0;

  constructor(deps: InvokeDeps, config: Partial<InvokeSkillConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get spawnsCount(): number { return this._spawnsCount; }
  get lastSpawnedId(): string { return this._lastSpawnedId; }
  get lastSpawnX(): number { return this._lastSpawnX; }
  get lastSpawnY(): number { return this._lastSpawnY; }
  get lastSpawnWasFallback(): boolean { return this._lastSpawnWasFallback; }
  get fizzled(): boolean { return this._fizzled; }
  get resolvedHp(): number { return this._resolvedHp; }
  get resolvedAggroWeight(): number { return this._resolvedAggroWeight; }
  get resolvedDriftSpeed(): number { return this._resolvedDriftSpeed; }
  get resolvedSpawnDistanceTiles(): number { return this._resolvedSpawnDistanceTiles; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Freshness fizzle check — consumes cooldown but does not spawn
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    const pos = this.deps.player.getPosition();
    const facing = this.deps.player.getFacingDirection();

    // Resolve spawn distance: base + additive from Outstretched (uses 'summon' scope)
    const spawnDistanceBonus = this.deps.resolver.getAdditiveEffect('summon', 'spawnDistanceTiles');
    const distanceTiles = this.config.baseSpawnDistanceTiles + spawnDistanceBonus;
    this._resolvedSpawnDistanceTiles = distanceTiles;

    const spawnX = pos.x + facing.x * distanceTiles * this.config.tileSize;
    const spawnY = pos.y + facing.y * distanceTiles * this.config.tileSize;

    // Wall collision fallback
    let finalX = spawnX;
    let finalY = spawnY;
    let wasFallback = false;
    if (this.deps.collision.isWallAt(spawnX, spawnY)) {
      finalX = pos.x;
      finalY = pos.y;
      wasFallback = true;
    }

    // Resolve HP: Invoke base × hpMultiplier from Tenacious ('summon' scope)
    const hpMultiplier = this.deps.resolver.getMultiplicativeEffect('summon', 'hpMultiplier', 1.0);
    const resolvedHp = Math.round(this.config.baseHp * hpMultiplier);
    this._resolvedHp = resolvedHp;

    // Resolve aggro weight: max(0.8 Invoke baseline, aggroRedirectChance from Tenacious)
    // Tenacious contributes 0.75 which is < 0.8 — Invoke's baseline dominates.
    // If a future upgrade contributes > 0.8, that wins instead.
    const aggroOverride = this.deps.resolver.getMaxEffect('summon', 'aggroRedirectChance');
    const resolvedAggroWeight = aggroOverride !== undefined
      ? Math.max(this.config.baseAggroWeight, aggroOverride)
      : this.config.baseAggroWeight;
    this._resolvedAggroWeight = resolvedAggroWeight;

    // Resolve drift speed: base × driftSpeedMultiplier from Outstretched ('summon' scope)
    const driftMultiplier = this.deps.resolver.getMultiplicativeEffect('summon', 'driftSpeedMultiplier', 1.0);
    const resolvedDriftSpeed = this.config.baseDriftSpeed * driftMultiplier;
    this._resolvedDriftSpeed = resolvedDriftSpeed;

    const wispConfig: WispSummonConfig = {
      id: 'invoke_wisp',
      archetype: 'wisp',
      baseHp: resolvedHp,
      baseDamage: 0,
      baseSpeed: resolvedDriftSpeed,
      leashRadiusTiles: this.config.leashRadiusTiles,
      aggroWeight: resolvedAggroWeight,
      aiMode: 'tracking',
    };

    this._lastSpawnedId = this.deps.summons.spawn(wispConfig, finalX, finalY);
    this._spawnsCount++;
    this._lastSpawnX = finalX;
    this._lastSpawnY = finalY;
    this._lastSpawnWasFallback = wasFallback;

    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
